import { useEffect, useRef, useState } from 'react';
import { AlertCircle } from 'lucide-react';
import { LoadingState } from '@/components/ui';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/stores/authStore';
import { authService } from '@/services/auth';

export function OAuthCallback() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setAuth } = useAuthStore();
  const [error, setError] = useState<string | null>(null);
  // StrictMode runs effects twice in dev; only consume the tokens once.
  const handled = useRef(false);

  useEffect(() => {
    if (handled.current) return;
    handled.current = true;
    let redirectTimer: ReturnType<typeof setTimeout> | undefined;
    const failWith = (message: string) => {
      setError(message);
      redirectTimer = setTimeout(() => navigate('/login', { replace: true }), 3000);
    };

    const handleCallback = async () => {
      const token = searchParams.get('token');
      const refreshToken = searchParams.get('refreshToken');
      const errorParam = searchParams.get('error');

      if (errorParam) {
        failWith(errorParam);
        return;
      }

      if (!token || !refreshToken) {
        failWith('Missing authentication tokens');
        return;
      }

      try {
        // Fetch user profile with the new token
        const user = await authService.getCurrentUserWithToken(token);
        setAuth(user, token, refreshToken);
        // replace: keep the tokens in the URL out of browser history
        navigate('/', { replace: true });
      } catch (err) {
        console.error('OAuth callback error:', err);
        failWith('Failed to complete authentication');
      }
    };

    handleCallback();
    return () => clearTimeout(redirectTimer);
  }, [searchParams, setAuth, navigate]);

  if (error) {
    return (
      <div className="space-y-4">
        <div className="alert-danger" role="alert">
          <AlertCircle className="h-5 w-5 shrink-0" />
          <span>{error}</span>
        </div>
        <p className="text-center text-sm text-gray-500">{t('auth.redirectingToLogin')}</p>
      </div>
    );
  }

  return (
    <LoadingState label={t('auth.completingLogin')} />
  );
}
